// src/components/teacher/TeacherGrades.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import { SaveIcon, GradeIcon } from "../shared/icons/Icons";
import {
  USE_DUMMY_DATA,
  studentsByCourseId,
} from "./dummyData";

function TeacherGrades({ courses, loading: parentLoading }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(
    location.state?.courseId ? String(location.state.courseId) : "",
  );
  const [grades, setGrades] = useState({});
  const [saving, setSaving] = useState({});
  const [savedStatus, setSavedStatus] = useState({});

  useEffect(() => {
    if (location.state?.courseId) {
      setSelectedCourse(String(location.state.courseId));
    }
  }, [location.state]);

  useEffect(() => {
    if (selectedCourse) {
      fetchStudentsForCourse(selectedCourse);
    } else {
      setStudents([]);
    }
  }, [selectedCourse]);

  const fetchStudentsForCourse = async (courseId) => {
    setLoading(true);
    try {
      if (USE_DUMMY_DATA) {
        const data = studentsByCourseId(courseId);
        setStudents(data);
        const initialGrades = {};
        data.forEach((student) => {
          initialGrades[student.id] = student.grade || "";
        });
        setGrades(initialGrades);
        setSavedStatus({});
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/teachers/me/courses/${courseId}/students`,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch students");
      }

      const data = await response.json();
      setStudents(Array.isArray(data) ? data : []);

      const initialGrades = {};
      data.forEach((student) => {
        initialGrades[student.id] = student.grade || "";
      });
      setGrades(initialGrades);
      setSavedStatus({});
    } catch (err) {
      console.error("Failed to fetch students for course:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGradeChange = (studentId, value) => {
    setGrades((prev) => ({ ...prev, [studentId]: value }));
    setSavedStatus((prev) => ({ ...prev, [studentId]: false }));
  };

  const handleSaveGrade = async (studentId) => {
    if (!grades[studentId]?.trim()) return;

    setSaving((prev) => ({ ...prev, [studentId]: true }));
    try {
      if (USE_DUMMY_DATA) {
        await new Promise((r) => setTimeout(r, 400));
        setSavedStatus((prev) => ({ ...prev, [studentId]: true }));
        setTimeout(() => {
          setSavedStatus((prev) => ({ ...prev, [studentId]: false }));
        }, 2500);
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/teachers/me/students/${studentId}/grade`,
        {
          method: "PUT",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ grade: grades[studentId] }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save grade");
      }

      setSavedStatus((prev) => ({ ...prev, [studentId]: true }));
      setTimeout(() => {
        setSavedStatus((prev) => ({ ...prev, [studentId]: false }));
      }, 3000);
    } catch (err) {
      console.error("Failed to save grade:", err);
    } finally {
      setSaving((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  if (parentLoading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="tp-page-header">
        <button
          type="button"
          className="tp-btn tp-btn--ghost"
          onClick={() => navigate("/teacher/students")}
        >
          View Students
        </button>
      </div>

      <div className="tp-filters">
        <label htmlFor="tp-grade-course" style={{ fontWeight: 600, color: "#64748b" }}>
          Select Course:
        </label>
        <select
          id="tp-grade-course"
          className="tp-select"
          value={selectedCourse}
          onChange={(e) => setSelectedCourse(e.target.value)}
        >
          <option value="">Choose a course...</option>
          {/* Value is the real course_id — /courses/{id}/students expects it. */}
          {Array.from(
            new Map(courses.map((c) => [c.course_id, c])).values(),
          ).map((course) => (
            <option key={course.course_id} value={course.course_id}>
              {course.course_name || course.name}
              {course.class_name
                ? ` (${course.class_name}${course.section ? ` ${course.section}` : ""})`
                : ""}
            </option>
          ))}
        </select>
      </div>

      {selectedCourse ? (
        loading ? (
          <div className="tp-loading">
            <div className="tp-spinner" />
            <p>Loading students...</p>
          </div>
        ) : students.length > 0 ? (
          <div className="tp-table-wrap">
            <table className="tp-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Grade</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr key={student.id}>
                    <td>
                      <span className="tp-mono">{student.student_id}</span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{student.name}</td>
                    <td>
                      <input
                        className="tp-input"
                        style={{ width: 120, minWidth: 120 }}
                        type="text"
                        value={grades[student.id] || ""}
                        onChange={(e) =>
                          handleGradeChange(student.id, e.target.value)
                        }
                        placeholder="Enter grade"
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`tp-btn${
                          savedStatus[student.id] ? " tp-btn--success" : ""
                        }`}
                        onClick={() => handleSaveGrade(student.id)}
                        disabled={
                          saving[student.id] || !grades[student.id]?.trim()
                        }
                      >
                        {saving[student.id]
                          ? "Saving..."
                          : savedStatus[student.id]
                            ? "✓ Saved"
                            : (
                              <>
                                <SaveIcon /> Save
                              </>
                            )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="tp-empty">
            <GradeIcon />
            <h3>No students enrolled</h3>
            <p>No students are currently enrolled in this course.</p>
          </div>
        )
      ) : (
        <div className="tp-empty">
          <GradeIcon />
          <h3>Select a course</h3>
          <p>Choose a course above to start entering grades.</p>
        </div>
      )}
    </div>
  );
}

export default TeacherGrades;
